import { useNavigate, useLocation } from "react-router-dom";
import { Gauge, Layers, Library, Plus, Users, Building, BarChart3, Home, ShieldCheck } from "lucide-react";
import { useSimulations } from "@/hooks/useSimulations";

/**
 * Shared admin nav, organised into three logical groups (Harish 2026-06-18):
 *
 *   INSIGHTS  → Dashboard · Analytics
 *   MANAGE    → Clients · Cohorts · Users
 *   SIMS      → Sim Library · + New simulation
 *
 * Groups are rendered as a single chip strip with hairline dividers
 * between them and small uppercase labels above each cluster so the
 * organisation is visible at a glance. Active route highlighted in
 * fuchsia.
 *
 * Role rules:
 *   super_admin  → every chip in every group
 *   client_admin → Dashboard + Cohorts only (the only admin pages they
 *                   can read today). Group labels are hidden when a
 *                   group reduces to ≤1 chip so the nav doesn't read
 *                   "INSIGHTS [Dashboard]  |  MANAGE [Cohorts]" for them.
 *   learner      → component returns null.
 *
 * `extraTrailing` slot lets a page tack on its own page-specific
 * buttons (Reload, "+ New cohort", etc) at the far right.
 * `showHome=false` hides the leading Home chip (used on /simulations
 * which IS the home page).
 */
interface AdminNavProps {
  extraTrailing?: React.ReactNode;
  showHome?: boolean;
}

type Role = "super_admin" | "client_admin";

interface NavItem {
  to: string;
  label: string;
  Icon: typeof Gauge;
  roles: Role[];
  match: string[];
  /** Fuchsia-styled "primary" chip (used for "+ New simulation"). */
  primary?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  {
    label: "Insights",
    items: [
      { to: "/dashboard",       label: "Dashboard", Icon: Gauge,       roles: ["super_admin", "client_admin"], match: ["/dashboard"] },
      { to: "/admin/analytics", label: "Analytics", Icon: BarChart3,   roles: ["super_admin"],                  match: ["/admin/analytics"] },
      { to: "/admin/audit",     label: "Audit log", Icon: ShieldCheck, roles: ["super_admin"],                  match: ["/admin/audit"] },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/admin/clients",   label: "Clients",  Icon: Building, roles: ["super_admin"],                  match: ["/admin/clients"] },
      { to: "/admin/cohorts",   label: "Cohorts",  Icon: Layers,   roles: ["super_admin", "client_admin"], match: ["/admin/cohorts"] },
      { to: "/admin/users",     label: "Users",    Icon: Users,    roles: ["super_admin"],                  match: ["/admin/users"] },
    ],
  },
  {
    label: "Sims",
    items: [
      { to: "/admin/sims",     label: "Sim Library",     Icon: Library, roles: ["super_admin"], match: ["/admin/sims"] },
      { to: "/admin/sims/new", label: "New simulation",  Icon: Plus,    roles: ["super_admin"], match: ["/admin/sims/new"], primary: true },
    ],
  },
];

const chipBase = "inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors";
const chipQuiet  = `${chipBase} border-white/10 bg-white/5 text-white/75 hover:bg-white/10`;
const chipActive = `${chipBase} border-fuchsia-400/50 bg-fuchsia-500/15 font-semibold text-fuchsia-100`;
const chipPrimary = `${chipBase} border-fuchsia-400/40 bg-fuchsia-500/15 font-semibold text-fuchsia-100 hover:bg-fuchsia-500/25`;

export function AdminNav({ extraTrailing, showHome = true }: AdminNavProps): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useSimulations();

  if (role !== "super_admin" && role !== "client_admin") return null;
  const r: Role = role;

  // Reduce each group to items the user can see.
  const visibleGroups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => it.roles.includes(r)) }))
    .filter((g) => g.items.length > 0);

  // Hide group labels when every group has 0 or 1 chip — labels are clutter
  // at that point (this is the client_admin case in practice).
  const showLabels = visibleGroups.some((g) => g.items.length > 1);

  return (
    <nav className="flex flex-wrap items-end gap-x-3 gap-y-2" aria-label="Admin sections">
      {showHome && (
        <button type="button" onClick={() => navigate("/simulations")} className={chipQuiet} title="Run a simulation">
          <Home className="h-3.5 w-3.5" /> Home
        </button>
      )}
      {visibleGroups.map((g, gIdx) => (
        <div key={g.label} className="flex items-end gap-3">
          {(showHome || gIdx > 0) && (
            <div className="hidden h-7 self-center sm:block" style={{ width: 1, background: "rgba(255,255,255,0.08)" }} />
          )}
          <div className="flex flex-col gap-1">
            {showLabels && (
              <span className="px-1 text-[9px] font-medium uppercase tracking-[0.15em] text-white/35">{g.label}</span>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {g.items.map(({ to, label, Icon, match, primary }) => {
                const active = match.some((m) => location.pathname.startsWith(m));
                const cls = active ? chipActive : primary === true ? chipPrimary : chipQuiet;
                return (
                  <button key={to} type="button" onClick={() => navigate(to)} className={cls}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
      {extraTrailing !== undefined && (
        <div className="ml-auto flex items-end gap-2">{extraTrailing}</div>
      )}
    </nav>
  );
}
